// Debug utilities for capturing and displaying errors

// Create an error capturing service that works independently of React
export class ErrorCaptureService {
  private errors: Array<{message: string; stack?: string; timestamp: Date}> = [];
  private originalConsoleError: typeof console.error;
  private originalWindowOnerror: typeof window.onerror;
  private domContainer: HTMLDivElement | null = null;
  
  constructor() {
    // Save original methods
    this.originalConsoleError = console.error;
    this.originalWindowOnerror = window.onerror;
    
    // Setup error listeners
    this.setupErrorListeners();
  }
  
  private setupErrorListeners() {
    // Override console.error
    console.error = (...args: any[]) => {
      this.captureError(args.join(' '));
      this.originalConsoleError.apply(console, args);
    };
    
    // Override window.onerror
    window.onerror = (message, source, lineno, colno, error) => {
      this.captureError(
        `${message} at ${source}:${lineno}:${colno}`, 
        error?.stack
      );
      
      if (this.originalWindowOnerror) {
        return this.originalWindowOnerror(message, source, lineno, colno, error);
      }
      return false;
    };
    
    // Add unhandled promise rejection listener
    window.addEventListener('unhandledrejection', (event) => {
      this.captureError(
        `Unhandled Promise Rejection: ${event.reason}`,
        event.reason?.stack
      );
    });
  }
  
  public captureError(message: string, stack?: string) {
    const error = {
      message,
      stack,
      timestamp: new Date()
    };
    
    this.errors.push(error);
    this.updateDomDisplay();
  }
  
  public getErrors() {
    return [...this.errors];
  }
  
  public clearErrors() {
    this.errors = [];
    this.updateDomDisplay();
  }
  
  public createDomContainer() {
    if (document.getElementById('error-capture-container')) {
      return;
    }
    
    this.domContainer = document.createElement('div');
    this.domContainer.id = 'error-capture-container';
    this.domContainer.style.position = 'fixed';
    this.domContainer.style.bottom = '10px';
    this.domContainer.style.right = '10px';
    this.domContainer.style.maxWidth = '80%';
    this.domContainer.style.maxHeight = '50%';
    this.domContainer.style.overflow = 'auto';
    this.domContainer.style.backgroundColor = 'rgba(255, 0, 0, 0.1)';
    this.domContainer.style.border = '1px solid red';
    this.domContainer.style.padding = '10px';
    this.domContainer.style.fontFamily = 'monospace';
    this.domContainer.style.fontSize = '12px';
    this.domContainer.style.zIndex = '10000';
    this.domContainer.style.color = 'black';
    this.domContainer.style.borderRadius = '4px';
    
    document.body.appendChild(this.domContainer);
    this.updateDomDisplay();
  }
  
  private updateDomDisplay() {
    if (!this.domContainer) {
      try {
        this.createDomContainer();
      } catch (e) {
        // Document might not be ready yet
        return;
      }
    }
    
    if (!this.domContainer) return;
    
    if (this.errors.length === 0) {
      this.domContainer.style.display = 'none';
      return;
    }
    
    this.domContainer.style.display = 'block';
    this.domContainer.innerHTML = `
      <div style="display: flex; justify-content: space-between; margin-bottom: 10px;">
        <strong>Runtime Errors (${this.errors.length})</strong>
        <button id="clear-errors-btn" style="font-size: 10px; background-color: #f44336; color: white; border: none; padding: 2px 5px; cursor: pointer; border-radius: 2px;">
          Clear
        </button>
      </div>
      <div style="max-height: 300px; overflow-y: auto;">
        ${this.errors.map((error, index) => `
          <div style="margin-bottom: 8px; padding: 5px; background-color: rgba(255,255,255,0.8); border-left: 3px solid red;">
            <div style="font-weight: bold; margin-bottom: 3px;">
              Error #${index + 1} (${error.timestamp.toLocaleTimeString()}):
            </div>
            <div style="color: #d32f2f;">${error.message}</div>
            ${error.stack ? `<pre style="font-size: 10px; margin-top: 5px; white-space: pre-wrap; color: #555;">${error.stack}</pre>` : ''}
          </div>
        `).join('')}
      </div>
    `;
    
    // Add event listener for the clear button
    document.getElementById('clear-errors-btn')?.addEventListener('click', () => {
      this.clearErrors();
    });
  }
}

// Create and export a singleton instance
export const errorCapture = new ErrorCaptureService();

// Function to initialize the error capture service
export function initErrorCapture() {
  // Will be called after DOM is ready
  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    errorCapture.createDomContainer();
  } else {
    document.addEventListener('DOMContentLoaded', () => {
      errorCapture.createDomContainer();
    });
  }
  
  // Also create after a short delay as a fallback
  setTimeout(() => {
    errorCapture.createDomContainer();
  }, 1000);
  
  return errorCapture;
} 